import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useToast, Toast as ToastType, ToastType as ToastTypeEnum } from '../contexts/ToastContext';

const Toast: React.FC<{ toast: ToastType }> = ({ toast }) => {
  const { removeToast } = useToast();

  const getIconAndColor = (type: ToastTypeEnum) => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5" />,
          bgColor: 'bg-gradient-to-r from-green-500/10 to-emerald-500/10',
          borderColor: 'border-green-500/30',
          iconColor: 'text-green-500'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-5 h-5" />,
          bgColor: 'bg-gradient-to-r from-red-500/10 to-pink-500/10',
          borderColor: 'border-red-500/30',
          iconColor: 'text-red-500'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          bgColor: 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10',
          borderColor: 'border-yellow-500/30',
          iconColor: 'text-yellow-500'
        };
      case 'info':
        return {
          icon: <Info className="w-5 h-5" />,
          bgColor: 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10',
          borderColor: 'border-blue-500/30',
          iconColor: 'text-blue-500'
        };
    }
  };

  const { icon, bgColor, borderColor, iconColor } = getIconAndColor(toast.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, x: 20 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: 20, x: 20 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`${bgColor} border ${borderColor} rounded-lg p-4 backdrop-blur-xl shadow-lg max-w-sm`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 ${iconColor} mt-0.5`}>{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium text-white">{toast.message}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                removeToast(toast.id);
              }}
              className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-blue-500/10"
            >
              {toast.action.label}
            </button>
          )}
          <button
            onClick={() => removeToast(toast.id)}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts } = useToast();

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast toast={toast} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
};
